import { FlexRow } from '@/components/common/Flex';
import { ArrowUp, ChevronDown, Minus } from '@/components/common/icons';
import { toDollarsWithCents } from '@/util/currency';
import { FlexProps, MenuItem } from '@chakra-ui/react';
import { BalanceChangeDateRange } from '@common/graphql/generated';
import { typedEntries } from '@common/util/objects';
import { capitalize } from '@common/util/strings';
import useLocalStorageState from 'use-local-storage-state';
import ActionMenu from './ActionMenu';
import { Button, MenuButton } from './Button';
import { Text } from './Typography';

const balanceChangeDateRangeLabelMap: Record<BalanceChangeDateRange, string> = {
    LAST_7: 'last 7 days',
    LAST_30: 'last 30 days',
    LAST_90: 'last 90 days',
    PAST_YEAR: 'past year',
    ALL_TIME: 'all time',
};

const getBalanceChangeDescription = (
    dateRange: BalanceChangeDateRange
): string => {
    return balanceChangeDateRangeLabelMap[dateRange];
};

export const useBalanceChangeDateRangeState = () => {
    // Remove old key that used lowercase strings
    localStorage.removeItem('balanceChangeDateRange');

    return useLocalStorageState<BalanceChangeDateRange>(
        'balanceChangeDateRangeType',
        { defaultValue: 'LAST_30' }
    );
};

export interface BalanceChangeProps extends FlexProps {
    dateRange: BalanceChangeDateRange;
    onChangeDateRange?: (dateRange: BalanceChangeDateRange) => void;
    change: number;
    tooltip?: string;
    noChangeText?: string;
    swapColors?: boolean;
    noText?: boolean;
    noDescription?: boolean;
}

const BalanceChange = ({
    change,
    dateRange,
    onChangeDateRange,
    noChangeText = 'No change in',
    swapColors,
    noText,
    noDescription,
    ...props
}: BalanceChangeProps) => {
    const [color, arrowDeg] = getColorAndArrowAngle(change, swapColors);
    const absBalance = Math.abs(change);
    const hasChange = change !== 0;

    return (
        <FlexRow
            display="inline-flex"
            fontSize="md"
            fontWeight={100}
            color="gray.400"
            alignItems="center"
            gap={0.5}
            {...props}
        >
            {hasChange ? (
                <>
                    <ArrowUp
                        size="sm"
                        color={color}
                        transform={`rotate(${arrowDeg})`}
                    />
                    {!noText && (
                        <Text
                            fontSize="sm"
                            color={color}
                            fontWeight={500}
                            letterSpacing="tight"
                            mr={1}
                        >
                            {toDollarsWithCents(absBalance)}
                        </Text>
                    )}
                </>
            ) : noText ? (
                <Minus size="sm" color="gray.400" />
            ) : (
                <Text color="gray.400" fontSize="sm" letterSpacing="tight">
                    {noChangeText}
                </Text>
            )}
            {!noText && !noDescription && (
                <>
                    {' '}
                    {onChangeDateRange ? (
                        <ActionMenu
                            menuButton={
                                <MenuButton
                                    as={Button}
                                    size="sm"
                                    variant="unstyled"
                                    h={'18px'}
                                    onClickEvent="Balance Change Date Range Button Clicked"
                                >
                                    <Text
                                        color="gray.400"
                                        fontSize="sm"
                                        letterSpacing="tight"
                                        fontWeight={100}
                                    >
                                        {hasChange
                                            ? capitalize(
                                                  getBalanceChangeDescription(
                                                      dateRange
                                                  )
                                              )
                                            : getBalanceChangeDescription(
                                                  dateRange
                                              )}
                                        <ChevronDown ml={0.5} size="xs" />
                                    </Text>
                                </MenuButton>
                            }
                        >
                            {typedEntries(balanceChangeDateRangeLabelMap).map(
                                ([value, label]) => {
                                    return (
                                        <MenuItem
                                            key={value}
                                            onClick={() =>
                                                onChangeDateRange(value)
                                            }
                                        >
                                            {label}
                                        </MenuItem>
                                    );
                                }
                            )}
                        </ActionMenu>
                    ) : (
                        <Text
                            color="gray.400"
                            fontSize="sm"
                            letterSpacing="tight"
                        >
                            {getBalanceChangeDescription(dateRange)}
                        </Text>
                    )}
                </>
            )}
        </FlexRow>
    );
};

const getColorAndArrowAngle = (
    balance: number,
    swapColors?: boolean
): [string, string] => {
    const greenColor = 'green.600';
    const redColor = 'red.500';

    if (balance > 0) return [swapColors ? redColor : greenColor, '0deg'];
    if (balance < 0) return [swapColors ? greenColor : redColor, '180deg'];
    return ['gray.400', '0deg'];
};

export default BalanceChange;
